import { zodResolver } from '@hookform/resolvers/zod';
import {
  configMgmtControllerAddFeeComponentToRisk,
  configMgmtControllerCreateRiskLevel,
  configMgmtControllerDeleteRiskLevel,
  configMgmtControllerListFeeComponentTypes,
  configMgmtControllerRemoveRiskCategoryFeeComponent,
  configMgmtControllerUpdateRiskLevel,
} from '@kalba/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { Coins, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '../../../../components/ui/button';
import { DataTable, type DataTableColumn } from '../../../../components/ui/data-table';
import { Dialog, DialogContent, DialogFooter } from '../../../../components/ui/dialog';
import { Price } from '../../../../components/ui/price';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../components/ui/select';
import { usePermissions } from '../../../../hooks/use-permissions';
import { useAuthStore } from '../../../../stores/auth.store';
import { useRisksPageStore } from '../../../../stores/config/risks-page.store';

const riskSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  applyTo: z.enum(['NEW', 'RENEWAL']),
  status: z.enum(['ACTIVE', 'INACTIVE']),
});

const feeLineSchema = z.object({
  typeId: z.string().min(1, 'Select fee type'),
  amount: z
    .string()
    .min(1)
    .refine((v) => Number(v) > 0, 'Amount must be > 0'),
  payee: z.string().optional(),
});
import { cn } from '../../../../lib/utils';
import { PermitTypeShell } from './components/PermitTypeShell';
import type { PermitTypeDetail, RiskLevel } from './components/types';

export const Route = createFileRoute('/config/request-types/$requestTypeId/risks')({
  beforeLoad: () => {
    const { user, permissions } = useAuthStore.getState();
    if (!user) throw redirect({ to: '/login' });
    if (!permissions.includes('config:view')) throw redirect({ to: '/dashboard' });
  },
  component: RisksPage,
});

// ── Helpers ───────────────────────────────────────────────────────────────────

function generateRiskCode(name: string): string {
  return (
    name
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, '_')
      .replace(/^_|_$/g, '') || 'RISK'
  );
}

// ── Types ─────────────────────────────────────────────────────────────────────

interface MasterFeeType {
  id: string;
  name: string;
  code: string;
  status: string;
}

interface FeeLine {
  feeComponentTypeId: string;
  feeTypeName: string;
  feeTypeCode: string;
  amount: string;
  payee: string;
}

// ── Fee component card ────────────────────────────────────────────────────────

function FeeLineCard({
  name,
  code,
  payee,
  amount,
  onRemove,
}: {
  name: string;
  code: string;
  payee?: string | null;
  amount: string | number;
  onRemove?: () => void;
}) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-emerald-50">
        <Coins size={16} className="text-emerald-500" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-900 truncate">{name}</p>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="inline-block rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 font-mono text-[10px] text-gray-500">
            {code}
          </span>
          {payee && <span className="text-[11px] text-gray-400">→ {payee}</span>}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Price
          value={amount}
          className="text-sm font-semibold text-gray-800"
          iconClassName="text-gray-500 w-[0.85em] h-[0.85em]"
        />
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="rounded-lg p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-500 transition-colors"
          >
            <Trash2 size={14} />
          </button>
        )}
      </div>
    </div>
  );
}

// ── Inline add-fee form ───────────────────────────────────────────────────────

function AddFeeForm({
  availableTypes,
  activeTypes,
  onAdd,
  onCancel,
}: {
  availableTypes: MasterFeeType[];
  activeTypes: MasterFeeType[];
  onAdd: (typeId: string, amount: string, payee: string) => void;
  onCancel: () => void;
}) {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<z.infer<typeof feeLineSchema>>({
    resolver: zodResolver(feeLineSchema),
    defaultValues: { typeId: '', amount: '', payee: '' },
  });

  return (
    <form
      onSubmit={handleSubmit((d) => onAdd(d.typeId, d.amount, d.payee ?? ''))}
      className="rounded-xl border border-brand-200 bg-brand-50/40 px-4 py-4 space-y-3"
    >
      <p className="text-xs font-semibold uppercase tracking-wide text-brand-700">
        Add Fee Component
      </p>
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <label className="mb-1 block text-[11px] font-medium text-gray-500">
            Fee Component Type
          </label>
          <Controller
            name="typeId"
            control={control}
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger className="h-9 border-gray-300 text-xs rounded-xl">
                  <SelectValue placeholder="Select type…" />
                </SelectTrigger>
                <SelectContent>
                  {availableTypes.length === 0 ? (
                    <div className="px-3 py-2 text-xs text-gray-400">
                      {activeTypes.length === 0
                        ? 'No fee component types defined yet.'
                        : 'All active types already added.'}
                    </div>
                  ) : (
                    availableTypes.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        <span className="font-medium">{t.name}</span>
                        <span className="ml-2 font-mono text-[10px] text-gray-400">{t.code}</span>
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            )}
          />
          {errors.typeId && <p className="mt-1 text-xs text-error-500">{errors.typeId.message}</p>}
        </div>
        <div>
          <label className="mb-1 block text-[11px] font-medium text-gray-500">Payee</label>
          <input
            type="text"
            placeholder="e.g. Beeah, Municipality"
            className="w-full rounded-xl border border-gray-300 px-2.5 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-brand-100 focus:border-brand-400"
            {...register('payee')}
          />
        </div>
        <div>
          <label className="mb-1 block text-[11px] font-medium text-gray-500">Amount</label>
          <input
            type="number"
            min="0"
            step="0.01"
            placeholder="0.00"
            className="w-full rounded-xl border border-gray-300 px-2.5 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-brand-100 focus:border-brand-400"
            {...register('amount')}
          />
          {errors.amount && <p className="mt-1 text-xs text-error-500">{errors.amount.message}</p>}
        </div>
      </div>
      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="text-xs text-gray-500 hover:text-gray-700 transition-colors"
        >
          Cancel
        </button>
        <Button variant="primary" size="sm" type="submit">
          <Plus size={12} />
          Add
        </Button>
      </div>
    </form>
  );
}

// ── Unified Create / Edit dialog ──────────────────────────────────────────────

function RiskCategoryDialog({
  permitType,
  risk,
  onClose,
}: {
  permitType: PermitTypeDetail;
  risk?: RiskLevel;
  onClose: () => void;
}) {
  const isEdit = !!risk;
  const qc = useQueryClient();

  const { register, handleSubmit, watch, control } = useForm<z.infer<typeof riskSchema>>({
    resolver: zodResolver(riskSchema),
    defaultValues: {
      name: risk?.name ?? '',
      applyTo: risk?.applyTo ?? 'NEW',
      status: risk?.status === 'INACTIVE' ? 'INACTIVE' : 'ACTIVE',
    },
  });

  // Edit mode: track existing fee components removed during this session
  const [removedFeeIds, setRemovedFeeIds] = useState<Set<string>>(new Set());
  // Both modes: new fee lines queued for creation on save
  const [newFeeLines, setNewFeeLines] = useState<FeeLine[]>([]);
  const [showAddFeeForm, setShowAddFeeForm] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const { data: masterTypes = [] } = useQuery<MasterFeeType[]>({
    queryKey: ['allFeeComponentTypes'],
    queryFn: () => configMgmtControllerListFeeComponentTypes() as Promise<MasterFeeType[]>,
  });

  const activeTypes = masterTypes.filter((t) => t.status === 'ACTIVE');

  // Existing fee components visible (not staged for removal)
  const visibleExisting = (risk?.feeComponents ?? []).filter((fc) => !removedFeeIds.has(fc.id));

  // Filter out already-assigned types from the "add" picker
  const assignedTypeIds = new Set([
    ...visibleExisting.map((fc) => fc.feeComponentTypeId),
    ...newFeeLines.map((fl) => fl.feeComponentTypeId),
  ]);
  const availableTypes = activeTypes.filter((t) => !assignedTypeIds.has(t.id));

  const totalFee =
    visibleExisting.reduce((s, fc) => s + Number(fc.amount), 0) +
    newFeeLines.reduce((s, fl) => s + Number(fl.amount), 0);
  const totalCount = visibleExisting.length + newFeeLines.length;

  function addFeeLine(typeId: string, amount: string, payee: string) {
    const type = activeTypes.find((t) => t.id === typeId);
    if (!type) return;
    setNewFeeLines((prev) => [
      ...prev,
      {
        feeComponentTypeId: type.id,
        feeTypeName: type.name,
        feeTypeCode: type.code,
        amount,
        payee,
      },
    ]);
    setShowAddFeeForm(false);
  }

  const onSave = handleSubmit(async (data) => {
    setSaveError(null);
    setIsSaving(true);
    try {
      if (isEdit) {
        await configMgmtControllerUpdateRiskLevel({
          id: permitType.id,
          riskId: risk?.id,
          requestBody: {
            name: data.name.trim(),
            applyTo: data.applyTo,
            status: data.status,
            displayOrder: risk?.displayOrder,
          },
        });
        for (const feeId of removedFeeIds) {
          await configMgmtControllerRemoveRiskCategoryFeeComponent({
            id: permitType.id,
            riskId: risk?.id,
            linkId: feeId,
          });
        }
        for (const fl of newFeeLines) {
          await configMgmtControllerAddFeeComponentToRisk({
            id: permitType.id,
            riskId: risk?.id,
            requestBody: {
              feeComponentTypeId: fl.feeComponentTypeId,
              amount: fl.amount,
              payee: fl.payee || undefined,
            },
          });
        }
      } else {
        const created = await configMgmtControllerCreateRiskLevel({
          id: permitType.id,
          requestBody: {
            name: data.name.trim(),
            code: generateRiskCode(data.name.trim()),
            displayOrder: 0,
            applyTo: data.applyTo,
          },
        });
        const newRiskId = (created as { id: string }).id;
        for (const fl of newFeeLines) {
          await configMgmtControllerAddFeeComponentToRisk({
            id: permitType.id,
            riskId: newRiskId,
            requestBody: {
              feeComponentTypeId: fl.feeComponentTypeId,
              amount: fl.amount,
              payee: fl.payee || undefined,
            },
          });
        }
      }
      qc.invalidateQueries({ queryKey: ['permitType', permitType.id] });
      onClose();
    } catch (err: unknown) {
      try {
        const body = (await (err as { response?: Response }).response?.json()) as
          | { message?: string }
          | undefined;
        setSaveError(body?.message ?? `Failed to ${isEdit ? 'update' : 'create'} risk category`);
      } catch {
        setSaveError(`Failed to ${isEdit ? 'update' : 'create'} risk category`);
      }
    } finally {
      setIsSaving(false);
    }
  });

  const canSave = !!watch('name')?.trim();

  const saveLabel = isSaving
    ? 'Saving…'
    : isEdit
      ? 'Save Changes'
      : totalCount > 0
        ? `Create with ${totalCount} fee component${totalCount !== 1 ? 's' : ''}`
        : 'Create Risk Category';

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !isSaving) onClose();
      }}
    >
      <DialogContent className="max-w-2xl p-0 gap-0 sm:rounded-2xl overflow-hidden">
        {/* Header */}
        <div className="border-b border-gray-100 px-6 py-5 pr-14">
          <h2 className="text-base font-semibold text-gray-900">
            {isEdit ? 'Edit Risk Category' : 'New Risk Category'}
          </h2>
          <p className="mt-0.5 text-sm text-gray-500">
            {isEdit
              ? 'Update name, applies-to, status and fee components'
              : 'Define a risk category and assign fee components'}
          </p>
        </div>

        {/* Scrollable body */}
        <div
          className="overflow-y-auto px-6 py-5 space-y-5"
          style={{ maxHeight: 'calc(100dvh - 220px)' }}
        >
          {/* Basic fields */}
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-600">
                Name <span className="text-error-500">*</span>
              </label>
              <input
                type="text"
                placeholder="e.g. High Risk, Low Impact"
                className="w-full rounded-xl border border-gray-300 bg-white px-3.5 py-2.5 text-sm text-gray-900 placeholder-gray-400 hover:border-gray-400 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100 transition-all"
                {...register('name')}
              />
            </div>

            <div className={isEdit ? '' : 'col-span-2'}>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-600">
                Applies To <span className="text-error-500">*</span>
              </label>
              <Controller
                name="applyTo"
                control={control}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger className="h-10 border-gray-300 rounded-xl text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="NEW">New Permit</SelectItem>
                      <SelectItem value="RENEWAL">Renewal Permit</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            {isEdit && (
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-600">
                  Status
                </label>
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger className="h-10 border-gray-300 rounded-xl text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ACTIVE">Active</SelectItem>
                        <SelectItem value="INACTIVE">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="border-t border-gray-100" />

          {/* Fee Components section */}
          <div>
            <div className="mb-3 flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                  Fee Components
                </p>
                <p className="mt-0.5 text-[11px] text-gray-400">
                  {totalCount > 0
                    ? `${totalCount} component${totalCount !== 1 ? 's' : ''} added`
                    : 'Add fee components that apply to this risk category'}
                </p>
              </div>
              {totalFee > 0 && (
                <span className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-50 border border-emerald-200 px-3 py-1.5 shrink-0">
                  <Price
                    value={totalFee}
                    className="text-sm font-semibold text-emerald-700"
                    iconClassName="text-emerald-500 w-[0.85em] h-[0.85em]"
                  />
                </span>
              )}
            </div>

            {/* Cards */}
            {(visibleExisting.length > 0 || newFeeLines.length > 0) && (
              <div className="space-y-2 mb-2">
                {visibleExisting.map((fc) => (
                  <FeeLineCard
                    key={fc.id}
                    name={fc.feeComponentType.name}
                    code={fc.feeComponentType.code}
                    payee={fc.payee}
                    amount={fc.amount}
                    onRemove={() => setRemovedFeeIds((prev) => new Set([...prev, fc.id]))}
                  />
                ))}
                {newFeeLines.map((fl) => (
                  <FeeLineCard
                    key={fl.feeComponentTypeId}
                    name={fl.feeTypeName}
                    code={fl.feeTypeCode}
                    payee={fl.payee}
                    amount={fl.amount}
                    onRemove={() =>
                      setNewFeeLines((prev) =>
                        prev.filter((x) => x.feeComponentTypeId !== fl.feeComponentTypeId),
                      )
                    }
                  />
                ))}
              </div>
            )}

            {/* Add form or dashed trigger */}
            {showAddFeeForm ? (
              <AddFeeForm
                availableTypes={availableTypes}
                activeTypes={activeTypes}
                onAdd={addFeeLine}
                onCancel={() => setShowAddFeeForm(false)}
              />
            ) : (
              <button
                type="button"
                onClick={() => setShowAddFeeForm(true)}
                disabled={availableTypes.length === 0}
                className={cn(
                  'flex w-full items-center justify-center gap-2 rounded-xl border border-dashed py-3 text-sm font-medium transition-colors',
                  availableTypes.length === 0
                    ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                    : 'border-gray-300 bg-white text-gray-500 hover:border-brand-300 hover:bg-brand-50 hover:text-brand-600',
                )}
              >
                <Plus size={15} />
                {availableTypes.length === 0 && activeTypes.length > 0
                  ? 'All fee component types added'
                  : 'Add Fee Component'}
              </button>
            )}
          </div>

          {saveError && (
            <div className="rounded-xl border border-error-200 bg-error-50 px-4 py-3 text-sm text-error-600">
              {saveError}
            </div>
          )}
        </div>

        {/* Footer */}
        <DialogFooter className="border-t border-gray-100 px-6 py-4">
          <Button variant="outline" size="sm" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button variant="success" size="sm" onClick={onSave} disabled={isSaving || !canSave}>
            {saveLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Main tab ──────────────────────────────────────────────────────────────────

function RisksTab({ permitType }: { permitType: PermitTypeDetail }) {
  const { can } = usePermissions();
  const qc = useQueryClient();
  const canEdit = can('config:edit');

  const {
    showCreate,
    editRisk: _editRisk,
    deleteConfirm,
    setShowCreate,
    setEditRisk,
    setDeleteConfirm,
  } = useRisksPageStore();
  const editRisk = _editRisk as RiskLevel | null;

  const invalidate = () => qc.invalidateQueries({ queryKey: ['permitType', permitType.id] });

  const deleteMutation = useMutation({
    mutationFn: (riskId: string) =>
      configMgmtControllerDeleteRiskLevel({ id: permitType.id, riskId }),
    onSuccess: () => {
      invalidate();
      setDeleteConfirm(null);
    },
  });

  const columns: DataTableColumn<RiskLevel>[] = [
    {
      key: 'code',
      header: 'Code',
      render: (r) => (
        <span className="rounded-md border border-gray-200 bg-gray-50 px-2 py-0.5 font-mono text-xs font-semibold text-gray-600">
          {r.code}
        </span>
      ),
    },
    {
      key: 'name',
      header: 'Name',
      render: (r) => <span className="font-medium text-gray-900">{r.name}</span>,
    },
    {
      key: 'applyTo',
      header: 'Applies To',
      render: (r) => (
        <span
          className={cn(
            'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold',
            r.applyTo === 'NEW'
              ? 'bg-blue-100 text-blue-700 border border-blue-200'
              : 'bg-amber-100 text-amber-700 border border-amber-200',
          )}
        >
          {r.applyTo === 'NEW' ? 'New Permit' : 'Renewal'}
        </span>
      ),
    },
    {
      key: 'totalFee',
      header: 'Total Fee',
      align: 'right',
      render: (r) => {
        const total = Number(r.totalFee ?? 0);
        return total > 0 ? (
          <Price
            value={total}
            className="text-xs font-semibold text-gray-800"
            iconClassName="text-gray-500 w-[0.85em] h-[0.85em]"
          />
        ) : (
          <span className="text-xs text-gray-400">—</span>
        );
      },
    },
    {
      key: 'feeCount',
      header: 'Components',
      align: 'center',
      render: (r) => (
        <span className="inline-flex items-center justify-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
          {r.feeComponents?.length ?? 0}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (r) => (
        <span
          className={cn(
            'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold',
            r.status === 'ACTIVE'
              ? 'bg-success-50 text-success-700 border border-success-200'
              : 'bg-gray-100 text-gray-500 border border-gray-200',
          )}
        >
          {r.status === 'ACTIVE' ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    ...(canEdit
      ? [
          {
            key: 'actions',
            header: '',
            render: (r: RiskLevel) =>
              deleteConfirm === r.id ? (
                <div className="flex items-center gap-1">
                  <span className="text-xs text-gray-500">Delete?</span>
                  <button
                    type="button"
                    onClick={() => deleteMutation.mutate(r.id)}
                    disabled={deleteMutation.isPending}
                    className="text-xs font-medium text-error-600"
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeleteConfirm(null)}
                    className="text-xs text-gray-500"
                  >
                    No
                  </button>
                </div>
              ) : (
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={() => setEditRisk(r)}
                    className="rounded px-2.5 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-800 transition-colors"
                    title="Edit"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeleteConfirm(r.id)}
                    className="rounded p-1 text-red-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                    title="Delete"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ),
          } satisfies DataTableColumn<RiskLevel>,
        ]
      : []),
  ];

  return (
    <div>
      <DataTable
        title="Risk Categories"
        description="Define risk categories for this permit type. Click Edit to manage fee components."
        headerActions={
          canEdit ? (
            <Button variant="primary" size="sm" onClick={() => setShowCreate(true)}>
              <Plus size={14} />
              New Risk Category
            </Button>
          ) : null
        }
        columns={columns}
        data={permitType.riskLevels}
        keyField="id"
        emptyMessage="No risk categories defined"
      />

      {showCreate && (
        <RiskCategoryDialog permitType={permitType} onClose={() => setShowCreate(false)} />
      )}

      {editRisk && (
        <RiskCategoryDialog
          permitType={permitType}
          risk={editRisk}
          onClose={() => setEditRisk(null)}
        />
      )}
    </div>
  );
}

function RisksPage() {
  const { requestTypeId } = Route.useParams();
  return (
    <PermitTypeShell requestTypeId={requestTypeId}>
      {(pt) => <RisksTab permitType={pt} />}
    </PermitTypeShell>
  );
}
